import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { RootState } from "src/redux/store";
import { KeyValuePairsProps } from "@amzn/awsui-components-console";
import { ClaimSubmittedStatus, LineOfBusiness } from "src/app/types";
import {
  getCargoClaim,
  resetTimeoutWorkaround,
  setValueInCargoAppState,
} from "src/app/Cargo/cargoAppSlice";
import { CargoTimeoutFailureMessage } from "src/views/claimSubmittedUtils";

const CARGO_TIMEOUT_WAIT_MS = 90000;

interface UseCargoTimeoutWorkaroundProps {
  lineOfBusiness: LineOfBusiness;
  claimModel: unknown;
  setSubmittedData: (data: KeyValuePairsProps.Pair[]) => void;
}

/**
 * Manages the Cargo Stage 3 timeout workaround.
 * When a timeout occurs during Cargo submission, waits 90 seconds
 * then checks the claim status to determine if it actually succeeded.
 */
export const useCargoTimeoutWorkaround = ({
  lineOfBusiness,
  claimModel,
  setSubmittedData,
}: UseCargoTimeoutWorkaroundProps): {
  isTimeoutWorkaroundActive: boolean;
} => {
  const dispatch = useAppDispatch();
  const {
    isTimeoutWorkaround,
    timeoutWorkaroundStartTime,
    claimNumber,
    loadingCargoClaim,
    cargoSubmittedStage,
  } = useAppSelector((state: RootState) => state.cargoApp);

  const isActive =
    isTimeoutWorkaround && lineOfBusiness === LineOfBusiness.CARGO;

  // Timer: wait 90 seconds then check claim status
  useEffect(() => {
    if (!isTimeoutWorkaround || !timeoutWorkaroundStartTime || !claimNumber) {
      return;
    }
    const elapsedTime = Date.now() - timeoutWorkaroundStartTime;
    const remainingTime = Math.max(0, CARGO_TIMEOUT_WAIT_MS - elapsedTime);

    const timerId = window.setTimeout(() => {
      dispatch(getCargoClaim({ claimNumber }));
    }, remainingTime);

    return () => window.clearTimeout(timerId);
  }, [isTimeoutWorkaround, timeoutWorkaroundStartTime, claimNumber, dispatch]);

  // Handle the result of the getCargoClaim call
  useEffect(() => {
    if (!isTimeoutWorkaround || loadingCargoClaim || !cargoSubmittedStage) {
      return;
    }

    dispatch(resetTimeoutWorkaround());

    if (cargoSubmittedStage === "STAGE3") {
      dispatch(
        setValueInCargoAppState({
          key: "submitScreenStatus",
          value: ClaimSubmittedStatus.SUCCESS,
        })
      );
      setSubmittedData([
        { label: "Claim Reference Number", value: claimNumber },
      ]);
    } else if (cargoSubmittedStage === "STAGE2") {
      dispatch(
        setValueInCargoAppState({
          key: "submitScreenStatus",
          value: ClaimSubmittedStatus.ERROR,
        })
      );
      setSubmittedData([
        {
          label: "",
          value: <CargoTimeoutFailureMessage claimModel={claimModel} />,
        },
      ]);
    }
  }, [
    isTimeoutWorkaround,
    loadingCargoClaim,
    cargoSubmittedStage,
    dispatch,
    claimNumber,
    claimModel,
    setSubmittedData,
  ]);

  return { isTimeoutWorkaroundActive: isActive };
};
